import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  Stethoscope,
  RefreshCcw,
  Eye,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PER_PAGE = 20;

const ESTADO_OPCIONES = [
  { value: "__all__", label: "Todos los estados" },
  { value: "pendiente_pago", label: "Pendiente de pago" },
  { value: "procesando", label: "Procesando" },
  { value: "requiere_info", label: "Requiere info" },
  { value: "completada", label: "Completada" },
  { value: "fallida", label: "Fallida" },
  { value: "cancelada", label: "Cancelada" },
];

const ESTADO_STYLES = {
  completada: "bg-emerald-100 text-emerald-800 border-emerald-200",
  procesando: "bg-blue-100 text-blue-800 border-blue-200",
  pendiente_pago: "bg-amber-100 text-amber-800 border-amber-200",
  requiere_info: "bg-purple-100 text-purple-800 border-purple-200",
  fallida: "bg-rose-100 text-rose-800 border-rose-200",
  cancelada: "bg-muted text-muted-foreground border-border",
};

function EstadoChip({ estado }) {
  const cls = ESTADO_STYLES[estado] || "bg-muted text-muted-foreground border-border";
  const label = ESTADO_OPCIONES.find((o) => o.value === estado)?.label || estado || "—";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
      data-testid={`consulta-estado-${estado}`}
    >
      {label}
    </span>
  );
}

function fmtFecha(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminConsultas() {
  const { user } = useAuth();
  const isSuperadmin = Boolean(user?.is_superadmin);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [inputQ, setInputQ] = useState("");
  const [estado, setEstado] = useState("__all__");
  const [loading, setLoading] = useState(true);

  const [detalleId, setDetalleId] = useState(null);
  const [detalleData, setDetalleData] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (estado && estado !== "__all__") params.set("estado", estado);
      params.set("page", String(page));
      params.set("per_page", String(PER_PAGE));
      const { data } = await api.get(`/admin/consultas?${params}`);
      setRows(data.items);
      setTotal(data.meta.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, estado, page]);

  useEffect(() => {
    load();
  }, [load]);

  const abrirDetalle = async (id) => {
    if (!isSuperadmin) {
      toast.error("Sólo superadmin puede leer el contenido descifrado.");
      return;
    }
    setDetalleId(id);
    setDetalleData(null);
    setDetalleLoading(true);
    try {
      const { data } = await api.get(`/admin/consultas/${id}`);
      setDetalleData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetalleId(null);
    } finally {
      setDetalleLoading(false);
    }
  };

  const cerrarDetalle = () => {
    setDetalleId(null);
    setDetalleData(null);
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div data-testid="admin-consultas">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#0F2942] flex items-center gap-2">
            <Stethoscope size={18} className="text-[#2563EB]" />
            Consultas con IA
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {total} consulta{total === 1 ? "" : "s"} registrada{total === 1 ? "" : "s"}.
            {isSuperadmin
              ? " Como superadmin puedes abrir el detalle completo (síntomas + respuesta IA descifrados) con auditoría reforzada."
              : " Metadatos únicamente. El contenido descifrado sólo lo puede ver un superadmin."}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="gap-1.5 self-start"
          data-testid="consultas-refrescar-btn"
        >
          <RefreshCcw size={14} /> Refrescar
        </Button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQ(inputQ.trim());
        }}
        className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
      >
        <div className="relative flex-1 max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <Input
            value={inputQ}
            onChange={(e) => setInputQ(e.target.value)}
            placeholder="Buscar por nombre o email de la persona usuaria"
            className="pl-9 h-9"
            data-testid="consultas-buscar-input"
          />
        </div>
        <div className="w-full sm:w-56">
          <Select
            value={estado}
            onValueChange={(v) => {
              setPage(1);
              setEstado(v);
            }}
          >
            <SelectTrigger data-testid="consultas-estado-select" className="h-9">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {ESTADO_OPCIONES.map((o) => (
                <SelectItem
                  key={o.value}
                  value={o.value}
                  data-testid={`consultas-estado-opt-${o.value}`}
                >
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" size="sm" variant="outline" data-testid="consultas-buscar-btn">
          Buscar
        </Button>
        {(q || estado !== "__all__") && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setInputQ("");
              setQ("");
              setEstado("__all__");
              setPage(1);
            }}
            data-testid="consultas-limpiar-btn"
          >
            Limpiar
          </Button>
        )}
      </form>

      <div className="mt-4 border border-border rounded-lg bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="consultas-tabla">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Fecha</th>
                <th className="text-left px-4 py-2 font-semibold">Persona usuaria</th>
                <th className="text-left px-4 py-2 font-semibold">Estado</th>
                <th className="text-left px-4 py-2 font-semibold">Monto</th>
                <th className="text-left px-4 py-2 font-semibold">Rondas</th>
                <th className="text-left px-4 py-2 font-semibold">GPC citadas</th>
                <th className="text-left px-4 py-2 font-semibold">Proveedor IA</th>
                <th className="text-right px-4 py-2 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    <Loader2 size={14} className="inline-block animate-spin mr-2" /> Cargando…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-muted-foreground"
                    data-testid="consultas-vacio"
                  >
                    Sin consultas para los criterios de búsqueda.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-border hover:bg-muted/30"
                    data-testid={`consulta-row-${r.id}`}
                  >
                    <td className="px-4 py-2 whitespace-nowrap">{fmtFecha(r.created_at)}</td>
                    <td className="px-4 py-2 min-w-[220px]">
                      <div className="font-medium text-[#0F2942]">{r.nombre_usuario}</div>
                      <div className="text-[11px] text-muted-foreground break-all">{r.email}</div>
                    </td>
                    <td className="px-4 py-2">
                      <EstadoChip estado={r.estado} />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {r.monto_label === "comped" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#2563EB]/10 text-[#2563EB] px-2 py-0.5 text-[11px] font-semibold">
                          <Sparkles size={10} /> Comped
                        </span>
                      ) : (
                        <span className="font-mono text-xs">{r.monto_label}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground text-center">
                      {r.clarificacion_rounds}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {r.gpc_codes && r.gpc_codes.length > 0 ? r.gpc_codes.join(", ") : "—"}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground font-mono">
                      {r.provider_used || "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {isSuperadmin ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 text-xs border-[#2563EB]/40 text-[#2563EB] hover:bg-[#2563EB]/5"
                          onClick={() => abrirDetalle(r.id)}
                          data-testid={`consulta-ver-completa-btn-${r.id}`}
                          title="Ver contenido descifrado (superadmin)"
                        >
                          <Eye size={12} /> Ver completa
                        </Button>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">Sólo superadmin</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div
          className="mt-4 flex items-center justify-between text-xs text-muted-foreground"
          data-testid="consultas-paginacion"
        >
          <div>
            Página {page} de {totalPages} · {total} consulta{total === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
              data-testid="consultas-prev-btn"
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(page + 1)}
              data-testid="consultas-next-btn"
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={detalleId !== null}
        onOpenChange={(open) => (open ? null : cerrarDetalle())}
      >
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="consulta-detalle-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2942]">
              <Stethoscope size={16} className="text-[#2563EB]" /> Consulta completa
            </DialogTitle>
            <DialogDescription>
              Contenido descifrado con auditoría de superadmin. Toda apertura queda registrada en el audit log.
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#2563EB]/10 text-[#2563EB] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
                <ShieldCheck size={10} /> Vista superadmin
              </span>
            </DialogDescription>
          </DialogHeader>

          {detalleLoading && (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="consulta-detalle-loading">
              <Loader2 size={16} className="inline-block animate-spin mr-2" /> Descifrando y auditando…
            </div>
          )}

          {!detalleLoading && detalleData && (
            <div className="space-y-4 text-sm" data-testid="consulta-detalle-content">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Field label="ID" value={detalleData.id} mono />
                <Field label="Estado" node={<EstadoChip estado={detalleData.estado} />} />
                <Field label="Payment status" value={detalleData.payment_status || "—"} mono />
                <Field label="Modo entrada" value={detalleData.modo_entrada || "—"} />
                <Field
                  label="Rondas clarificación"
                  value={String(detalleData.clarificacion_rounds ?? 0)}
                />
                <Field
                  label="Proveedor IA"
                  value={detalleData.provider_used || "—"}
                  mono
                />
                <Field
                  label="Fuente descifrado"
                  value={detalleData.respuesta_source || "—"}
                  mono
                />
                <Field
                  label="GPC citadas"
                  value={
                    detalleData.gpc_codes && detalleData.gpc_codes.length
                      ? detalleData.gpc_codes.join(", ")
                      : "—"
                  }
                  mono
                />
                <Field label="Fecha" value={fmtFecha(detalleData.created_at)} />
                <Field
                  label="Laboratorios vinculados"
                  value={String((detalleData.laboratorio_ids || []).length)}
                />
              </div>

              <Bloque label="Síntomas capturados">
                <div
                  className="whitespace-pre-wrap font-mono text-[12px] text-foreground"
                  data-testid="consulta-detalle-sintomas"
                >
                  {detalleData.sintomas || "(vacío)"}
                </div>
              </Bloque>

              {detalleData.preguntas && detalleData.preguntas.length > 0 && (
                <Bloque label="Preguntas de clarificación">
                  <ul className="list-disc pl-5 text-[12px] text-muted-foreground space-y-1">
                    {detalleData.preguntas.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </Bloque>
              )}

              {detalleData.clarificaciones && detalleData.clarificaciones.length > 0 && (
                <Bloque label="Respuestas del paciente a la clarificación">
                  <div className="space-y-2 text-[12px]">
                    {detalleData.clarificaciones.map((c, i) => (
                      <div
                        key={i}
                        className="border-l-2 border-[#2563EB]/30 pl-3 py-1"
                      >
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                          Ronda {i + 1}
                        </div>
                        <div className="whitespace-pre-wrap">
                          {typeof c === "string" ? c : c?.respuesta || JSON.stringify(c)}
                        </div>
                      </div>
                    ))}
                  </div>
                </Bloque>
              )}

              <Bloque label="Respuesta IA (descifrada)">
                <div
                  className="whitespace-pre-wrap text-[12px] text-foreground"
                  data-testid="consulta-detalle-respuesta"
                >
                  {detalleData.respuesta_ia || "(no descifrable o vacío)"}
                </div>
              </Bloque>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={cerrarDetalle}
              data-testid="consulta-detalle-cerrar-btn"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, node, mono }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={`text-foreground mt-0.5 break-all ${mono ? "font-mono text-[11px]" : "text-xs"}`}
      >
        {node || value || "—"}
      </div>
    </div>
  );
}

function Bloque({ label, children }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}
